import { useState } from "react";
import { useOpenFromQuery } from "../../hooks/useOpenFromQuery";
import {
  Card,
  Group,
  Text,
  Button,
  Badge,
  ActionIcon,
  Menu,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { adminsApi } from "../../api/admins";
import { adminRoleLabel, type Admin } from "../../types/admin";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { PageHeader } from "../../components/common/PageHeader";
import { SearchInput } from "../../components/common/SearchInput";
import { DataTable, type Column } from "../../components/common/DataTable";
import { ExportMenu } from "../../components/common/ExportMenu";
import { collectAll, type ExportField } from "../../lib/export";
import { formatDate } from "../../lib/format";
import { errorMessage } from "../../lib/axios";
import { AdminFormModal, type AdminFormValues } from "./AdminFormModal";

const adminExportFields: ExportField<Admin>[] = [
  { label: "Name", value: (a) => a.name },
  { label: "Email", value: (a) => a.email },
  { label: "Role", value: (a) => a.role },
  { label: "Phone", value: (a) => a.phoneNumber },
  { label: "Added", value: (a) => a.createdAt },
];

export function AdminsPage() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  useOpenFromQuery(() => setFormOpen(true));
  const [editing, setEditing] = useState<Admin | null>(null);

  const { page, setPage, search, onSearchChange, result, isLoading, error } =
    usePaginatedList<Admin>({ queryKey: ["admins"], fetcher: adminsApi.list });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["admins"] });
  }

  const saveMutation = useMutation({
    mutationFn: (values: AdminFormValues) =>
      editing
        ? adminsApi.update(editing.id ?? editing._id ?? "", {
            name: values.name,
            email: values.email,
            phoneNumber: values.phoneNumber,
            role: values.role,
          })
        : adminsApi.create(values),
    onSuccess: () => {
      notifications.show({
        color: "green",
        message: editing ? "Admin updated" : "Admin created",
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminsApi.remove(id),
    onSuccess: () => {
      notifications.show({ color: "green", message: "Admin removed" });
      invalidate();
    },
    onError: (e) =>
      notifications.show({ color: "red", message: errorMessage(e) }),
  });

  function confirmDelete(a: Admin) {
    modals.openConfirmModal({
      title: "Remove admin",
      children: (
        <Text size="sm">
          Remove <b>{a.name}</b> ({a.email})? They will lose access to the console.
        </Text>
      ),
      labels: { confirm: "Remove", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(a.id ?? a._id ?? ""),
    });
  }

  const columns: Column<Admin>[] = [
    {
      key: "name",
      header: "Name",
      render: (a) => (
        <Text size="sm" fw={600}>
          {a.name}
        </Text>
      ),
    },
    { key: "email", header: "Email", render: (a) => a.email },
    {
      key: "role",
      header: "Role",
      render: (a) => (
        <Badge variant="light">
          {adminRoleLabel(a.role)}
        </Badge>
      ),
    },
    { key: "phone", header: "Phone", render: (a) => a.phoneNumber ?? "—" },
    {
      key: "created",
      header: "Added",
      render: (a) => (
        <Text size="xs" c="dimmed">
          {formatDate(a.createdAt)}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (a) => (
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray">
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={16} />}
              onClick={() => {
                setEditing(a);
                setFormOpen(true);
              }}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => confirmDelete(a)}
            >
              Remove
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Admin Users"
        subtitle="Console access & roles"
        actions={
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            New admin
          </Button>
        }
      />
      <Card withBorder radius="md" padding="lg">
        <Group mb="md" gap="sm">
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search name or email…"
          />
          <ExportMenu
            filename="admins"
            fields={adminExportFields}
            fetchAll={() =>
              collectAll(adminsApi.list, { search: search || undefined })
            }
          />
        </Group>
        <DataTable
          columns={columns}
          rows={result?.items ?? []}
          rowKey={(a) => a.id ?? a._id ?? a.email}
          loading={isLoading}
          error={error ? errorMessage(error) : null}
          emptyLabel="No admins yet"
          page={page}
          totalPages={result?.totalPages}
          total={result?.total}
          onPageChange={setPage}
        />
      </Card>

      <AdminFormModal
        opened={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        editing={editing}
        loading={saveMutation.isPending}
        onSubmit={(values) => saveMutation.mutate(values)}
      />
    </>
  );
}
